/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'underscore',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../lib/@waca/dashboard-common/dist/api/impl/CallbackInvoker',
	'../../../lib/@waca/dashboard-common/dist/utils/ActionTypes',
	'../../../api/DashboardAPI',
	'./ToolbarDockAPI'
], function(_, APIFactory, CallbackInvoker, ActionTypes, DashboardAPI, ToolbarDockAPI){
	const DOCK_PREFERENCE_KEY = 'isToolbarDocked';
	class ToolbarDock extends CallbackInvoker {

		constructor({ features }){
			const dashboard = features.API;
			const logger = dashboard.getService(DashboardAPI.GLOBAL_SERVICES.LOGGER);
			super({ logger });

			this._dashboard = dashboard;
			this._contentActions = features.ContentActions;
			this._dashboardState = features.DashboardState;
			this._dashboardPreferences = features.DashboardPreferences;

			this._dockedToolbarState = {
				actions: [],
				selectedIds: []
			};
		}

		initialize(){
			const isExplore = this._dashboard.getType().toUpperCase()==='EXPLORE';
			if (!isExplore) {
				this._api = APIFactory.createAPI(this, [ToolbarDockAPI, CallbackInvoker]);

				let dockedState = this._dashboardPreferences.getPreference(DOCK_PREFERENCE_KEY);
				if(dockedState === undefined) {
					this._dashboardPreferences.setPreference(DOCK_PREFERENCE_KEY, true);
					this._dashboardPreferences.savePreferences().catch((error) => {
						this._logger.error('unable to save dashboard preferences', error.message);
					});
				}

				this._dashboard.getCanvasWhenReady().then((canvas) => {
					this._canvas = canvas;
					canvas.on('change:selections:select', this.onContentSelectionChange, this);
					canvas.on('change:selections:deselect', this.onContentSelectionChange, this);
					this._dashboard.on('dashboard:show', this.onDashboardShow, this);
					this._updateCanvasToolbar();
					this._currentUIState = Object.assign({}, this._dashboardState.getUiState());
					this._dashboardState.onUiStateChange(this._uiStateChange.bind(this));
				});

			} else {
				this._api = undefined;
			}
		}

		getAPI(){
			return this._api;
		}

		destroy(){
			if(this._canvas) {
				this._canvas.off('change:selections:select', this.onContentSelectionChange, this);
				this._canvas.off('change:selections:deselect', this.onContentSelectionChange, this);
			}
			if(this._dashboard){
				this._dashboard.off('dashboard:show', this.onDashboardShow, this);
			}
			this._api = null;
		}

		getOwnerIDs() {
			return this._dockedToolbarState.selectedIds;
		}

		onDashboardShow() {
			if(this.isContentToolbarDocked()) {
				//isContentToolbarDocked check is redundant with the check inside _renderContentToolbar.
				//Ideally _renderContentToolbar should either render empty or render the view
				this._renderContentToolbar();
			} else {
				this.updateODTState([], []);
			}
		}

		onContentSelectionChange() {
			this._renderContentToolbar();
		}

		_getSelectedIds() {
			let selectedIds = [];
			if (this._currentUIState.focus) {
				const widgets = this._canvas.getWidgets();
				for (let i=0; i<widgets.length; i++) {
					if (widgets[i].isWidgetMaximized()) {
						selectedIds.push(widgets[i].getId());
						break;
					}
				}
			} else {
				const currentSelection = this._canvas.getSelectedContentList();
				selectedIds = currentSelection.map(content => content.getId());
				selectedIds.sort();
			}
			return selectedIds;
		}

		_renderContentToolbar(focusToolbar = true, forceRender = false) {
			if(this.isContentToolbarDocked()) {
				const selectedIds = this._getSelectedIds();
				if (selectedIds.length === 0) {
					this.updateODTState([]);
				} else {
					if (forceRender || !_.isEqual(this._dockedToolbarState.selectedIds, selectedIds)) {
						this.updateODTState(this._contentActions.getContentActionList(selectedIds), selectedIds);
						if (focusToolbar && selectedIds.length) {
							this.focusODT();
						}
					}
				}
			}
		}

		/** API implementations */

		/**
		 * @implements ToolbarDockAPI#toggle
		 * @description will toggle the toolbar state to change from dock/undock. We do this currently by triggering a click on the
		 * selection which will prompt the toolbar to re-render
		 *
		 * Note: We are triggering a click to show/hide the floating toolbar
		 */
		toggle() {
			let docked = !this.isContentToolbarDocked();
			this._dashboardPreferences.setPreference(DOCK_PREFERENCE_KEY, docked);
			this._dashboardPreferences.savePreferences().catch((error) => {
				this._logger.error('Unable to save dashboard preferences', error.message);
			});
			const currentSelection = this._canvas.getSelectedContentList();

			if(docked === true) {
				this._renderContentToolbar();
			} else {
				this.updateODTState([], []);
			}
			if (currentSelection.length > 0) {
				document.getElementById(currentSelection[0].getId()).click();
			}
		}

		/**
		 * @implements ToolbarDockAPI#disableDockedODT
		 */
		disableDockedODT() {
			this._dashboardPreferences.setPreference(DOCK_PREFERENCE_KEY, false);
			this._dashboardPreferences.savePreferences();
			this.updateODTState([], []);
		}

		/**
		 * @implements ToolbarDockAPI#enableDockedODT
		 */
		enableDockedODT() {
			this._dashboardPreferences.setPreference(DOCK_PREFERENCE_KEY, true);
			this._dashboardPreferences.savePreferences();
			this.updateODTState([], []);
		}

		/**
		 * @implements ToolbarDockAPI#updateODTState
		 */
		updateODTState(actions, selectedIds) {
			this._dockedToolbarState.actions = this._orderActions(actions);
			this._dockedToolbarState.selectedIds = selectedIds || undefined;
			this.invokeCallbacks(ToolbarDockAPI.UPDATE_DOCKED_TOOLBAR_STATE, this._dockedToolbarState);
		}

		/**
		 * @implements ToolbarDockAPI#isContentToolbarDocked
		 */
		isContentToolbarDocked() {
			return this._dashboardPreferences.getPreference(DOCK_PREFERENCE_KEY) || this._currentUIState.focus;
		}

		_uiStateChange(updatedState) {
			const previousState = this._currentUIState;
			this._currentUIState = Object.assign({}, updatedState);
			if (updatedState.focus || previousState.focus) {
				if (this.isContentToolbarDocked()) {
					this._renderContentToolbar(false, true);
				} else {
					this.updateODTState([], []);
				}

			}
			this._updateCanvasToolbar();
		}

		_updateCanvasToolbar() {
			this.invokeCallbacks(ToolbarDockAPI.UPDATE_CANVAS_TOOLBAR_STATE, this._getCanvasActions());
		}

		_getCanvasActions() {
			const actions = this._orderActions(this._contentActions.getContentActionList([]));
			return { actions };
		}

		_orderActions(actions) {
			actions.forEach(item => {
				if (item.order === undefined) {
					item.order = ActionTypes[item.name];
				}
			});

			actions.sort(function(a, b) {
				if ((a.order && b.order) || (!a.order && !b.order)) { //If both are defined compare. If both are undefined, return NaN, which leaves order unchanged.
					return a.order - b.order;
				}
				// defined order should be before undefined order
				if (a.order && !b.order) {
					// a before b
					return -1;
				}
				if (!a.order && b.order) {
					// b before a
					return 1;
				}
			});

			return actions;
		}

		/*
		 * @implements ToolbarDockAPI#focusODT
		 */
		focusODT() {
			this.invokeCallbacks(ToolbarDockAPI.FOCUS_DOCKED_TOOLBAR);
		}

	}
	return ToolbarDock;
});
